import { useEffect, useState, useCallback } from 'react';
import AppHeader from '../../components/AppHeader';
import { useAuth } from '../../contexts/MemberAuthContext';
import { containersApi, chaptersApi } from '../../api';

const emptyBox = { name: '', evidence_number: '', note: '' };
const emptyItem = { custom_name: '', unit: 'ks', quantity: 1 };

export default function Containers() {
  const { member } = useAuth();
  const needsChapterPick = !member.chapter_id;

  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState(member.chapter_id || '');
  const [boxes, setBoxes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyBox);
  const [openBox, setOpenBox] = useState(null);
  const [items, setItems] = useState([]);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [error, setError] = useState('');

  useEffect(() => { if (needsChapterPick) chaptersApi.list().then((r) => setChapters(r.data)).catch(() => {}); }, [needsChapterPick]);

  const load = useCallback(() => {
    if (!chapterId) { setBoxes([]); return; }
    containersApi.list(needsChapterPick ? chapterId : undefined).then((r) => setBoxes(r.data)).catch(() => setBoxes([]));
  }, [chapterId, needsChapterPick]);
  useEffect(() => { load(); }, [load]);

  const loadItems = (boxId) => containersApi.items(boxId).then((r) => setItems(r.data)).catch(() => setItems([]));
  const toggleBox = (boxId) => { if (openBox === boxId) { setOpenBox(null); } else { setOpenBox(boxId); setItemForm(emptyItem); loadItems(boxId); } };

  const createBox = async (e) => {
    e.preventDefault(); setError('');
    try {
      await containersApi.create({ ...(needsChapterPick ? { chapter_id: chapterId } : {}), name: form.name, evidence_number: form.evidence_number || null, note: form.note || null });
      setForm(emptyBox); setShowForm(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Vytvoření boxu selhalo.'); }
  };

  const deleteBox = async (id) => {
    if (!confirm('Smazat box včetně obsahu?')) return;
    setError('');
    try { await containersApi.delete(id); if (openBox === id) setOpenBox(null); load(); }
    catch (err) { setError(err.response?.data?.error || 'Smazání selhalo.'); }
  };

  const addItem = async (e, boxId) => {
    e.preventDefault(); setError('');
    if (!itemForm.custom_name.trim()) { setError('Zadejte název položky.'); return; }
    try {
      await containersApi.addItem(boxId, { custom_name: itemForm.custom_name, unit: itemForm.unit, quantity: Number(itemForm.quantity) || 0 });
      setItemForm(emptyItem); loadItems(boxId); load();
    } catch (err) { setError(err.response?.data?.error || 'Přidání položky selhalo.'); }
  };

  const saveItemQty = async (boxId, item, qty) => {
    const q = Number(qty); if (q === item.quantity || Number.isNaN(q)) return;
    await containersApi.updateItem(boxId, item.id, { item_name: item.item_name, unit: item.unit, quantity: q });
    loadItems(boxId);
  };

  const delItem = async (boxId, itemId) => { await containersApi.deleteItem(boxId, itemId); loadItems(boxId); load(); };

  return (
    <div>
      <AppHeader subtitle="Euroboxy" />
      <div className="container" style={{ paddingTop: 24, maxWidth: 720 }}>
        <p className="text-muted mb-16">Předpřipravené boxy s evidenčním číslem a vlastním obsahem. Box naložíš na cestu jedním klikem v detailu cesty.</p>

        {needsChapterPick && (
          <div className="form-group">
            <label>Oblastní spolek</label>
            <select className="form-input" value={chapterId} onChange={(e) => { setChapterId(e.target.value); setOpenBox(null); }}>
              <option value="">— vyberte spolek —</option>
              {chapters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {error && <div className="alert alert-warning mb-16">{error}</div>}

        {chapterId && (
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3>Boxy ({boxes.length})</h3>
            {!showForm && <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setForm(emptyBox); setError(''); }}>+ Nový box</button>}
          </div>
        )}

        {showForm && (
          <form className="card mb-16" onSubmit={createBox}>
            <div className="flex gap-8">
              <div className="form-group flex-1">
                <label>Název *</label>
                <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="např. ADMIN box" required />
              </div>
              <div className="form-group" style={{ width: 160 }}>
                <label>Evidenční číslo</label>
                <input className="form-input" value={form.evidence_number} onChange={(e) => setForm({ ...form, evidence_number: e.target.value })} placeholder="vlastní značení" />
              </div>
            </div>
            <div className="form-group">
              <label>Poznámka</label>
              <input className="form-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="flex gap-8">
              <button type="submit" className="btn btn-primary flex-1">Vytvořit box</button>
              <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setError(''); }}>Zrušit</button>
            </div>
          </form>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {boxes.map((b) => (
            <div key={b.id} className="card">
              <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => toggleBox(b.id)}>
                  <div style={{ fontWeight: 700 }}>{openBox === b.id ? '▾' : '▸'} 📦 {b.name}
                    {b.evidence_number && <span className="text-muted" style={{ fontWeight: 400 }}> · {b.evidence_number}</span>}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                    {b.item_count} položek{b.on_trip > 0 ? ' · 🚚 na cestě' : ''}
                  </div>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => deleteBox(b.id)}>Smazat</button>
              </div>

              {openBox === b.id && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
                  {items.length === 0 ? <p className="text-muted" style={{ fontSize: '0.85rem' }}>Box je prázdný.</p> : (
                    <table style={{ fontSize: '0.9rem' }}>
                      <tbody>
                        {items.map((it) => (
                          <tr key={it.id}>
                            <td>{it.item_name}</td>
                            <td style={{ width: 90 }}>
                              <input type="number" min="0" step="any" className="form-input" style={{ padding: '2px 6px', height: 32 }}
                                defaultValue={it.quantity} onBlur={(e) => saveItemQty(b.id, it, e.target.value)} />
                            </td>
                            <td style={{ width: 40 }}>{it.unit}</td>
                            <td style={{ width: 36 }}><button className="btn btn-danger btn-sm" onClick={() => delItem(b.id, it.id)}>×</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <form onSubmit={(e) => addItem(e, b.id)} style={{ marginTop: 8 }}>
                    <div className="flex gap-8" style={{ alignItems: 'flex-end' }}>
                      <div className="form-group flex-1" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.78rem' }}>Položka</label>
                        <input className="form-input" value={itemForm.custom_name} onChange={(e) => setItemForm({ ...itemForm, custom_name: e.target.value })} placeholder="např. Balík A4" />
                      </div>
                      <div className="form-group" style={{ width: 80, margin: 0 }}>
                        <label style={{ fontSize: '0.78rem' }}>Počet</label>
                        <input type="number" min="0" step="any" className="form-input" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })} />
                      </div>
                      <div className="form-group" style={{ width: 64, margin: 0 }}>
                        <label style={{ fontSize: '0.78rem' }}>Jedn.</label>
                        <input className="form-input" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} />
                      </div>
                      <button type="submit" className="btn btn-primary">+</button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ))}
          {chapterId && boxes.length === 0 && <p className="text-muted text-center">Zatím žádné boxy.</p>}
        </div>
      </div>
    </div>
  );
}
